/**
 * PlayScene — elevator-gurl 新仕様実装
 *
 * レイアウト (640×960):
 *
 *   [ エレベータシャフト | ビル断面（各階に行き先ボタン縦並び） ]
 *   ─────────────────────────────────────────────────────────
 *   |      | 8F [  8F  ]                                    |
 *   |      | 7F [  7F  ]                                    |
 *   | ELV  | 6F [  6F  ] 陳小明                             |
 *   |      | 5F [  5F  ]                                    |
 *   |      | 4F [  4F  ]                                    |
 *   |      | 3F [  3F  ] 李芳（赤）                          |
 *   |      | 2F [  2F  ]                                    |
 *   |      | 1F  待機列（乗り込み中 / input フェーズ）         |
 *   ────────────────────────────────────────────────────────
 *   [ HUD: ○N ×N / フェーズ表示 ]
 *
 * 階数が多い場合（FLOOR_COUNT > 8）は吹き出しオーバーレイに切り替える（将来対応）。
 */
import { Container, Graphics, Sprite, Text, Texture } from 'pixi.js'
import type { KeyboardCommand, KeyboardManager } from '../input/KeyboardManager'
import type { TouchManager } from '../input/TouchManager'
import {
  UI_PRIMARY,
  UI_SECONDARY,
  UI_TEXT_PRIMARY,
  UI_TEXT_DIM,
  TIME_PALETTE,
  SHAFT_PALETTE,
  NEON_RED,
  NEON_YELLOW,
  NEON_CYAN,
  NEON_ORANGE,
  LAMP_WARM,
} from '../constants/colors'
import { TOD_LABEL } from '../constants/labels'
import {
  MAX_WAITING,
  type ElevatorPhase,
  type GameState,
  type Passenger,
} from '../game/types'
import {
  createInitialState,
  updateElevator,
  playerPressFloor,
  floorToY,
  INPUT_TIMEOUT_MS,
  MAX_MISTAKES,
  TUITION_GOAL,
  timeOfDay,
} from '../game/logic'

// ─── レイアウト定数 ────────────────────────────────────────────
const VIEW_W = 640
const VIEW_H = 960

/** 1フロアの最小ピクセル高さ（高層階スクロールモード） */
const MIN_FLOOR_H = 52

/** エレベータシャフト幅 */
const SHAFT_W = 36
/** ビル右端X */
const BUILDING_RIGHT = VIEW_W - 8
/** ビル上端Y（HUDの下） */
const BUILDING_TOP = 48
/** ビル下端Y */
const BUILDING_BOTTOM = VIEW_H - 8

/** HUD: 上段テキストY */
const HUD_TOP_Y = 14
/** HUD: タイマーバーY（上段と下段の間） */
const HUD_TIMER_BAR_Y = 34
/** エレベータ箱のサイズ */
const ELEV_W = 28
// ELEV_H は floorH から動的に計算するため PlayScene.floorH を参照する

/** 行き先ボタンの領域 */
const BTN_X = SHAFT_W + 4 // ボタン左端X
const BTN_W = 72 // ボタン幅
// BTN_H は floorH から動的に計算するため PlayScene.floorH を参照する
const BTN_RADIUS = 4
/** ボタン右端からビル右端までのスペース（乗客名表示エリア） */
const NAME_X = BTN_X + BTN_W + 6

// ─── カラー ────────────────────────────────────────────────────
const COLOR_FLOOR_LINE = 0x4a3820
const COLOR_ELEV_BODY = 0xc8a85a
const COLOR_ELEV_DOOR = 0x8b6914
const COLOR_HUD_BG = 0x111111
const COLOR_BTN_EMPTY = 0x2a1f0e // 未押し
const COLOR_BTN_PLAYER = UI_PRIMARY // プレイヤーが押した
const COLOR_BTN_SELF = 0xaa2222 // 客自身が押した（不正解）
const COLOR_BTN_AUTO = 0x1a4a2a // 上の階から乗ってくる客（1階行き自動）
const COLOR_BTN_CHILD = 0xcc9922 // 子供の悪戯（黄色系）
const COLOR_DOOR_OPEN = 0x3a6a8a // 扉が開いているときの背景
/** auto 客（上の階から1F行き）の名前テキスト色 */
const COLOR_NAME_AUTO = 0x88cc88
/** 窓のガラス色 */
const COLOR_WINDOW_FILL = 0x3a5a7a
const COLOR_WINDOW_STROKE = 0x5a8aaa
/** 乗降オーバーレイの半透明背景色 */
const COLOR_OVERLAY_BG = 0x000000

/** inputフェーズのタイムバー色 */
const COLOR_TIMER_BAR = 0x44cc88
const COLOR_TIMER_LOW = 0xcc4444
/** 待機列テキスト色 */
const COLOR_WAITING_TEXT = 0xddcc88
const COLOR_WAITING_MORE = 0x888866
/** ログテキスト色 */
const COLOR_LOG_TEXT = 0xaaccff
/** ログ: 正解・正しく届けた（緑） */
const COLOR_LOG_CORRECT = 0x99ee99
/** ログ: ミス・客自押し（赤橙） */
const COLOR_LOG_MISS = 0xee6644
/** 乗客数インジケータ色（エレベータ箱の暗色に合わせる） */
const COLOR_PASSENGER_COUNT = COLOR_HUD_BG

/** 看板テキストプール（ネオン・張り紙・スローガン） */
const NEON_SIGNS = [
  '福',
  '囍',
  '招財進寶',
  '一路平安',
  '吉祥',
  '龍',
  '鳳',
  'ネコと和解せよ',
  '立入禁止',
  '요주의',
  '←非常口',
  '唐辛子屋',
  '易經占卜',
  '麻雀館',
  '理髮',
  '豆腐坊',
  '管理人室',
  '立食そば',
  '無断駐輪禁止',
  '工事中',
]
const NEON_COLORS = [NEON_RED, NEON_YELLOW, NEON_CYAN, NEON_ORANGE]

/** 乗降オーバーレイボックスの幅（SHAFT_W右端〜NAME_X手前くらいまで） */
const BOARDING_OVERLAY_W = 110

export class PlayScene extends Container {
  private state: GameState

  /** worldContainer: スクロールする描画要素のコンテナ */
  private worldContainer: Container

  /** 1フロアの高さ（floorCount 変化時に更新） */
  private floorH: number
  /** floorCount 変化通知用の前フレーム値 */
  private prevFloorCount: number
  /** ビル背景スプライト（public/assets/building-bg.png） */
  private readonly buildingBgSprite: Sprite
  /** チューリン スプライト（idle / reading を切り替え） */
  private readonly turingSprite: Sprite
  private readonly turingIdleTex: Texture
  private readonly turingReadingTex: Texture

  // ─── ゲームオーバー通知 ───────────────────────────────────────
  private _gameOverFired = false
  private onGameOver:
    | ((money: number, score: number, mistakes: number) => void)
    | null = null
  private onClear:
    | ((money: number, score: number, mistakes: number) => void)
    | null = null

  // ─── 乗降オーバーレイ ─────────────────────────────────────────
  private boardingOverlayLines: Array<{ text: string }> = []
  private readonly boardingOverlayGfx = new Graphics()
  private boardingOverlayTexts: Text[] = []

  // ─── フェーズ変化検知 ──────────────────────────────────────────
  private prevPhase: ElevatorPhase | null = null
  private prevPassengers: Passenger[] = []

  // ─── 乗降ログ ─────────────────────────────────────────────────
  private readonly logLines: Array<{
    text: string
    kind: 'normal' | 'correct' | 'miss'
  }> = []

  // ─── PixiJS オブジェクト ──────────────────────────────────────
  private readonly hudGfx = new Graphics()
  private readonly buildingBgGfx = new Graphics() // 背景矩形（毎フレーム時刻色で再描画）
  private readonly buildingStaticGfx = new Graphics() // 床ライン・外枠・窓（constructor で1回）
  private readonly elevGfx = new Graphics()
  private readonly btnLayerGfx = new Graphics() // ボタン背景
  private readonly timerGfx = new Graphics() // inputタイマーバー

  private readonly logGfx = new Graphics()

  private readonly hudText: Text
  private readonly phaseText: Text
  private readonly floorIndicatorText: Text
  private readonly passengerCountText: Text

  /** 住民タイプ別・コマ別テクスチャ */
  private readonly residentTextures: Record<string, [Texture, Texture]>
  /** 待機客スプライトプール */
  private waitingSpritePool: Sprite[] = []
  /** アニメーションフレームカウンタ（ms累積） */
  private animTimer = 0
  /** 現在のアニメコマ（0 or 1） */
  private animFrame = 0

  private waitingTextPool: Text[] = []
  private logTextPool: Text[] = []

  /**
   * 各階のボタン情報（2階〜FLOOR_COUNT階）
   * 1階は「待機列・乗降エリア」なのでボタンなし
   */
  private floorBtns: Array<{
    floor: number
    gfx: Graphics
    labelText: Text // "NF"
    nameText: Text // 押した客の名前
    neonText: Text // 看板・張り紙
  }> = []

  constructor() {
    super()
    this.state = createInitialState()
    this.floorH = (BUILDING_BOTTOM - BUILDING_TOP) / this.state.floorCount
    this.prevFloorCount = this.state.floorCount

    const baseStyle = {
      fontFamily: 'monospace',
      fill: UI_TEXT_PRIMARY,
    }

    // worldContainer（スクロール対象の描画要素を格納）
    this.worldContainer = new Container()
    this.addChild(this.worldContainer)

    // HUD（スクロールしない）
    this.addChild(this.hudGfx)

    this.hudText = new Text({
      text: '[朝] ○ 0  × 0  ¥ 0  ♥♥♥♥♥',
      style: { ...baseStyle, fontSize: 11 },
    })
    this.hudText.anchor.set(0, 0.5)
    this.hudText.x = 8
    this.hudText.y = HUD_TOP_Y
    this.addChild(this.hudText)

    this.phaseText = new Text({
      text: '',
      style: { ...baseStyle, fontSize: 11, fill: UI_TEXT_DIM },
    })
    this.phaseText.anchor.set(1, 0.5)
    this.phaseText.x = VIEW_W - 8
    this.phaseText.y = 38
    this.addChild(this.phaseText)

    // ビル断面（背景は毎フレーム更新、静的部分は1回）→ worldContainer へ
    this.worldContainer.addChild(this.buildingBgGfx)

    // ビル背景スプライト（ドット絵上書き用プレースホルダ）
    this.turingIdleTex = Texture.from('/assets/turing-idle.png')
    this.turingReadingTex = Texture.from('/assets/turing-reading.png')
    this.buildingBgSprite = new Sprite(Texture.from('/assets/building-bg.png'))
    this.buildingBgSprite.x = 0
    this.buildingBgSprite.y = BUILDING_TOP
    this.buildingBgSprite.width = VIEW_W
    this.buildingBgSprite.height = BUILDING_BOTTOM - BUILDING_TOP
    this.buildingBgSprite.alpha = 0.18 // プレースホルダは薄く; 本物ドット絵に差し替えたら 1.0 に
    this.worldContainer.addChild(this.buildingBgSprite)

    this.worldContainer.addChild(this.buildingStaticGfx)
    this.drawBuildingStatic()
    this.drawBuildingBg()

    // ボタン層 → worldContainer へ
    this.worldContainer.addChild(this.btnLayerGfx)

    // エレベータ（毎フレーム再描画）→ worldContainer へ
    this.worldContainer.addChild(this.elevGfx)

    // チューリンスプライト（ドット絵上書き用プレースホルダ）
    this.turingSprite = new Sprite(this.turingIdleTex)
    this.turingSprite.anchor.set(0.5, 1)
    this.turingSprite.alpha = 0.5 // プレースホルダは薄く
    this.worldContainer.addChild(this.turingSprite)

    // タイマーバー（HUD）→ this に直接
    this.addChild(this.timerGfx)

    // 待機列レイヤー → worldContainer へ（スプライト・テキストプールで描画）

    // 乗降ログレイヤー → worldContainer へ
    this.worldContainer.addChild(this.logGfx)

    // 乗降オーバーレイレイヤー → worldContainer へ
    this.worldContainer.addChild(this.boardingOverlayGfx)

    // 現在階インジケータ → worldContainer へ
    this.floorIndicatorText = new Text({
      text: '1F',
      style: { ...baseStyle, fontSize: 10 },
    })
    this.floorIndicatorText.anchor.set(0.5, 1)
    this.floorIndicatorText.x = SHAFT_W / 2
    this.worldContainer.addChild(this.floorIndicatorText)

    // 乗客数インジケータ（エレベータ箱内に重ねて表示）→ worldContainer へ
    this.passengerCountText = new Text({
      text: '',
      style: {
        fontFamily: 'monospace',
        fontSize: 9,
        fill: COLOR_PASSENGER_COUNT,
      },
    })
    this.passengerCountText.anchor.set(0.5, 0.5)
    this.worldContainer.addChild(this.passengerCountText)

    // 各階のボタンを生成（2階〜FLOOR_COUNT階）
    this.buildFloorButtons()

    // 住民スプライト用テクスチャをプリロード
    this.residentTextures = {
      normal: [
        Texture.from('/assets/resident-normal-0.png'),
        Texture.from('/assets/resident-normal-1.png'),
      ],
      elder: [
        Texture.from('/assets/resident-elder-0.png'),
        Texture.from('/assets/resident-elder-1.png'),
      ],
      child: [
        Texture.from('/assets/resident-child-0.png'),
        Texture.from('/assets/resident-child-1.png'),
      ],
    }
  }

  // ─── 静的描画 ──────────────────────────────────────────────────

  /** 時刻に応じたビル背景色を返す */
  private buildingColorForTime(minutes: number): number {
    const tod = timeOfDay(minutes)
    return TIME_PALETTE[tod].bg
  }

  /** シャフト色（時刻に応じて変化） */
  private shaftColorForTime(minutes: number): number {
    const tod = timeOfDay(minutes)
    return SHAFT_PALETTE[tod]
  }

  /** 背景矩形のみ再描画（毎フレーム時刻色で更新） */
  private drawBuildingBg(): void {
    const g = this.buildingBgGfx
    g.clear()

    const bgColor = this.buildingColorForTime(this.state.gameTimeMinutes)
    const shaftColor = this.shaftColorForTime(this.state.gameTimeMinutes)
    const buildingH = this.state.floorCount * this.floorH

    // ビル本体背景
    g.rect(SHAFT_W, BUILDING_TOP, BUILDING_RIGHT - SHAFT_W, buildingH)
    g.fill(bgColor)

    // シャフト
    g.rect(0, BUILDING_TOP, SHAFT_W, buildingH)
    g.fill(shaftColor)
  }

  /** 床ライン・外枠・窓を描画（constructor で1回のみ） */
  private drawBuildingStatic(): void {
    const g = this.buildingStaticGfx
    g.clear()

    const floorCount = this.state.floorCount
    const floorH = this.floorH
    const buildingH = floorCount * floorH

    // 各階の床ライン
    for (let f = 1; f <= floorCount; f++) {
      const y = floorToY(f, BUILDING_TOP, floorH, floorCount) + floorH
      g.rect(0, y - 1, BUILDING_RIGHT, 2)
      g.fill(COLOR_FLOOR_LINE)
    }

    // 建物外枠
    g.rect(0, BUILDING_TOP, BUILDING_RIGHT, buildingH)
    g.stroke({ color: COLOR_FLOOR_LINE, width: 2 })

    // 各階番号ラベルと窓
    for (let f = 1; f <= floorCount; f++) {
      const floorMidY =
        floorToY(f, BUILDING_TOP, floorH, floorCount) + floorH / 2

      // 窓（1階は待機エリアなのでなし）
      if (f > 1) {
        for (let w = 0; w < 2; w++) {
          const wx = NAME_X + 10 + w * 50
          const wy = floorMidY - 7
          g.rect(wx, wy, 14, 14)
          g.fill(COLOR_WINDOW_FILL)
          g.rect(wx, wy, 14, 14)
          g.stroke({ color: COLOR_WINDOW_STROKE, width: 1 })
        }
      }
    }
  }

  private buildFloorButtons(): void {
    const floorCount = this.state.floorCount
    const floorH = this.floorH
    const btnH = floorH * 0.62

    for (let floor = 2; floor <= floorCount; floor++) {
      const by =
        floorToY(floor, BUILDING_TOP, floorH, floorCount) + (floorH - btnH) / 2

      const gfx = new Graphics()
      this.drawFloorBtn(gfx, by, btnH, 'empty', null)
      gfx.eventMode = 'static'
      gfx.cursor = 'pointer'
      gfx.on('pointerdown', () => {
        this.onPlayerPressFloor(floor)
      })
      this.worldContainer.addChild(gfx)

      const labelText = new Text({
        text: `${floor}F`,
        style: {
          fontFamily: 'monospace',
          fontSize: 12,
          fontWeight: 'bold',
          fill: UI_TEXT_PRIMARY,
        },
      })
      labelText.anchor.set(0.5, 0.5)
      labelText.x = BTN_X + BTN_W / 2
      labelText.y = by + btnH / 2
      this.worldContainer.addChild(labelText)

      const nameText = new Text({
        text: '',
        style: { fontFamily: 'monospace', fontSize: 10, fill: UI_TEXT_PRIMARY },
      })
      nameText.anchor.set(0, 0.5)
      nameText.x = NAME_X
      nameText.y = by + btnH / 2
      this.worldContainer.addChild(nameText)

      // 看板テキスト（右余白にランダム配置）
      const sign = NEON_SIGNS[Math.floor(Math.random() * NEON_SIGNS.length)]
      const neonColor =
        NEON_COLORS[Math.floor(Math.random() * NEON_COLORS.length)]
      const neonText = new Text({
        text: sign,
        style: {
          fontFamily: 'monospace',
          fontSize: 9,
          fill: neonColor,
        },
      })
      neonText.anchor.set(1, 0.5)
      neonText.x = BUILDING_RIGHT - 4
      neonText.y = by + btnH / 2
      neonText.alpha = 0.75
      this.worldContainer.addChild(neonText)

      this.floorBtns.push({ floor, gfx, labelText, nameText, neonText })
    }
  }

  private drawFloorBtn(
    gfx: Graphics,
    by: number,
    btnH: number,
    state: 'empty' | 'player' | 'self' | 'auto' | 'child',
    _passenger: Passenger | null
  ): void {
    gfx.clear()
    const color =
      state === 'player'
        ? COLOR_BTN_PLAYER
        : state === 'self'
          ? COLOR_BTN_SELF
          : state === 'auto'
            ? COLOR_BTN_AUTO
            : state === 'child'
              ? COLOR_BTN_CHILD
              : COLOR_BTN_EMPTY

    gfx.roundRect(BTN_X, by, BTN_W, btnH, BTN_RADIUS)
    gfx.fill(color)
    const borderColor = state !== 'empty' ? UI_SECONDARY : COLOR_FLOOR_LINE
    gfx.roundRect(BTN_X, by, BTN_W, btnH, BTN_RADIUS)
    gfx.stroke({ color: borderColor, width: 1 })
  }

  // ─── 入力処理 ──────────────────────────────────────────────────

  private onPlayerPressFloor(floor: number): void {
    if (this.state.elevator.phase !== 'input') return
    this.state = playerPressFloor(this.state, floor)
    this.refreshButtons()
  }

  // ─── ボタン更新 ────────────────────────────────────────────────

  private refreshButtons(): void {
    const floorH = this.floorH
    const btnH = floorH * 0.62
    const floorCount = this.state.floorCount

    for (const btn of this.floorBtns) {
      const by =
        floorToY(btn.floor, BUILDING_TOP, floorH, floorCount) +
        (floorH - btnH) / 2

      // この階に向かっている乗客を探す
      const passenger =
        this.state.passengers.find(p => p.targetFloor === btn.floor) ?? null

      const btnState =
        passenger?.pressedBy === 'player'
          ? 'player'
          : passenger?.pressedBy === 'self'
            ? 'self'
            : passenger?.pressedBy === 'auto'
              ? 'auto'
              : passenger?.pressedBy === 'child'
                ? 'child'
                : 'empty'

      this.drawFloorBtn(btn.gfx, by, btnH, btnState, passenger)

      // 名前表示
      if (passenger && passenger.pressedBy !== null) {
        if (passenger.pressedBy === 'child') {
          btn.nameText.text = `⚡ ${passenger.resident.nameZh}`
          btn.nameText.style.fill = COLOR_BTN_CHILD
        } else {
          btn.nameText.text = passenger.resident.nameZh
          btn.nameText.style.fill =
            passenger.pressedBy === 'self'
              ? COLOR_BTN_SELF
              : passenger.pressedBy === 'auto'
                ? COLOR_NAME_AUTO
                : UI_TEXT_PRIMARY
        }
      } else {
        btn.nameText.text = ''
      }
    }
  }

  // ─── 毎フレーム更新 ────────────────────────────────────────────

  update(deltaMS: number): void {
    // アニメーションタイマー更新
    this.animTimer += deltaMS
    if (this.animTimer >= 400) {
      this.animTimer = 0
      this.animFrame = 1 - this.animFrame
    }

    this.state = updateElevator(this.state, deltaMS)

    // ランダムイベント検知
    if (this.state.pendingEvent) {
      const ev = this.state.pendingEvent
      const prefix = ev.kind === 'complaint' ? '😠' : '🎁'
      this.addLog(
        `${prefix}${ev.residentName}「${ev.text}」`,
        ev.kind === 'complaint' ? 'miss' : 'correct'
      )
      if (ev.moneyBonus > 0) {
        this.addLog(`  +¥${ev.moneyBonus}`, 'correct')
      }
      this.state = { ...this.state, pendingEvent: null }
    }

    // floorCount 変化時にレイアウトを再構築
    if (this.state.floorCount !== this.prevFloorCount) {
      this.addLog(`!! 違法建築で ${this.state.floorCount}Fになった！`, 'miss')
      this.rebuildLayout()
      this.prevFloorCount = this.state.floorCount
    }

    this.drawBuildingBg() // 時刻変化で背景色が変わるため毎フレーム更新
    this.drawElevator()
    this.drawHUD()
    this.drawTimerBar()
    this.refreshButtons()
    this.detectPhaseChange()
    this.refreshWaitingList()
    this.refreshLogArea()
    this.drawBoardingOverlay()

    this.prevPhase = this.state.elevator.phase
    this.prevPassengers = [...this.state.passengers]

    this.updateCamera()

    if (this.state.isGameOver && !this._gameOverFired) {
      this._gameOverFired = true
      if (this.state.isClear) {
        this.onClear?.(this.state.money, this.state.score, this.state.mistakes)
      } else {
        this.onGameOver?.(
          this.state.money,
          this.state.score,
          this.state.mistakes
        )
      }
    }
  }

  private updateCamera(): void {
    const floorCount = this.state.floorCount
    const totalH = MIN_FLOOR_H * floorCount
    if (totalH <= BUILDING_BOTTOM - BUILDING_TOP) {
      // 全景に収まる場合はスクロールしない
      this.worldContainer.y = 0
      return
    }
    // エレベーターの現在Y座標（world座標）を中心にカメラを合わせる
    const elev = this.state.elevator
    const floorH = this.floorH
    const buildingTop = BUILDING_TOP
    const elevWorldY = buildingTop + (floorCount - elev.currentFloor) * floorH
    // カメラ中心をエレベーターに合わせる
    const targetY = VIEW_H / 2 - elevWorldY
    // 上端・下端クランプ
    const minY = VIEW_H - 8 - (buildingTop + floorCount * floorH)
    const maxY = 0
    this.worldContainer.y = Math.max(minY, Math.min(maxY, targetY))
  }

  private drawElevator(): void {
    const g = this.elevGfx
    g.clear()

    const elev = this.state.elevator
    const floorH = this.floorH
    const floorCount = this.state.floorCount
    const elevH = floorH * 0.82
    const elevY = floorToY(elev.currentFloor, BUILDING_TOP, floorH, floorCount)

    // 扉開放時の背景ハイライト
    if (elev.phase === 'door_open') {
      const floorY = floorToY(
        Math.round(elev.currentFloor),
        BUILDING_TOP,
        floorH,
        floorCount
      )
      g.rect(0, floorY, BUILDING_RIGHT, floorH)
      g.fill({ color: COLOR_DOOR_OPEN, alpha: 0.18 })
    }

    // エレベータ箱
    const ex = (SHAFT_W - ELEV_W) / 2
    const ey = elevY + (floorH - elevH) / 2
    g.roundRect(ex, ey, ELEV_W, elevH, 3)
    g.fill(COLOR_ELEV_BODY)
    g.roundRect(ex, ey, ELEV_W, elevH, 3)
    g.stroke({ color: COLOR_ELEV_DOOR, width: 1 })

    // ドアの縦線
    g.moveTo(ex + ELEV_W / 2, ey)
    g.lineTo(ex + ELEV_W / 2, ey + elevH)
    g.stroke({ color: COLOR_ELEV_DOOR, width: 1 })

    // 現在階インジケータ
    const floor = Math.round(elev.currentFloor)
    this.floorIndicatorText.text = `${floor}F`
    this.floorIndicatorText.x = SHAFT_W / 2
    this.floorIndicatorText.y = elevY - 2

    // 乗客数インジケータ
    const count = this.state.passengers.length
    this.passengerCountText.text = count > 0 ? String(count) : ''
    this.passengerCountText.x = ex + ELEV_W / 2
    this.passengerCountText.y = ey + elevH - 6

    // チューリンスプライト: エレベーター箱の底中央に配置
    const isMoving = elev.phase === 'moving_up' || elev.phase === 'moving_down'
    this.turingSprite.texture = isMoving
      ? this.turingReadingTex
      : this.turingIdleTex
    this.turingSprite.x = ex + ELEV_W / 2
    this.turingSprite.y = ey + elevH - 2
  }

  private drawHUD(): void {
    const g = this.hudGfx
    g.clear()
    g.rect(0, 0, VIEW_W, BUILDING_TOP)
    g.fill(COLOR_HUD_BG)

    // 上段: 時刻・スコア・ミス・現金・満足度
    const tod = timeOfDay(this.state.gameTimeMinutes)
    const todLabel = TOD_LABEL[tod]
    const clamped = Math.min(this.state.mistakes, MAX_MISTAKES)
    const hearts = '♥'.repeat(MAX_MISTAKES - clamped) + '♡'.repeat(clamped)
    const remaining = Math.max(0, TUITION_GOAL - this.state.money)
    const scoreDisplay = Math.floor(this.state.score / 1000)
    this.hudText.text = `${todLabel} ¥${this.state.money}  残¥${remaining}  × ${this.state.mistakes}  ${hearts}  📖${scoreDisplay}`

    // 下段: フェーズ表示
    const phase = this.state.elevator.phase
    const phaseLabel: Record<typeof phase, string> = {
      boarding: '乗車中',
      input: '行き先を押せ',
      moving_up: '上昇中',
      door_open: '扉開',
      moving_down: '下降中',
    }
    this.phaseText.text = phaseLabel[phase]
  }

  private drawTimerBar(): void {
    const g = this.timerGfx
    g.clear()

    if (this.state.elevator.phase !== 'input') return

    const ratio = Math.max(
      0,
      this.state.elevator.doorTimerMs / INPUT_TIMEOUT_MS
    )
    const barW = (VIEW_W - 16) * ratio
    const color = ratio < 0.3 ? COLOR_TIMER_LOW : COLOR_TIMER_BAR

    g.rect(8, HUD_TIMER_BAR_Y, barW, 3)
    g.fill(color)
  }

  // ─── フェーズ変化検知・ログ追記 ────────────────────────────────

  private detectPhaseChange(): void {
    const phase = this.state.elevator.phase
    if (this.prevPhase === phase) return

    // door_open フェーズへの遷移時にオーバーレイ内容を決定する
    if (this.prevPhase !== 'door_open' && phase === 'door_open') {
      const currentFloor = Math.round(this.state.elevator.currentFloor)
      const passengerNames = new Set(
        this.state.passengers.map(p => p.resident.name)
      )

      const lines: Array<{ text: string }> = []

      if (currentFloor === 1) {
        // 1F boarding フェーズ: waitingQueue から上位3人のみ表示（階数は非表示 = 覚えていないと押せない設計）
        // passengerNames は1F分岐では参照しない（1F到着時には全員降車済みでpassengersは空のため）
        const queue = this.state.waitingQueue
        const showCount = Math.min(queue.length, 3)
        for (let i = 0; i < showCount; i++) {
          lines.push({ text: `↑ ${queue[i].nameZh}` })
        }
        const remaining = queue.length - showCount
        if (remaining > 0) {
          // 「...他N人」フォーマットは refreshWaitingList の待機列表示と共通の文言
          lines.push({ text: `...他${remaining}人` })
        }
      } else {
        // 上の階: 降りる客 + 乗ってくる客
        const alighting = this.state.passengers.filter(
          p => p.targetFloor === currentFloor
        )
        for (const p of alighting) {
          lines.push({ text: `↓ ${p.resident.nameZh}` })
        }

        const boarding = this.state.residents.filter(
          r => r.floor === currentFloor && !passengerNames.has(r.name)
        )
        for (const r of boarding) {
          lines.push({ text: `↑ ${r.nameZh} (${r.floor}F)` })
        }
      }

      this.boardingOverlayLines = lines
    }

    // door_open が終わった直後（afterDoorClose 実行済み）にログを記録する。
    // 乗降処理は afterDoorClose 内で行われるため、door_open への遷移時ではなく
    // door_open から次フェーズへの遷移時に prevPassengers と比較する。
    if (this.prevPhase === 'door_open') {
      // オーバーレイをクリア（door_open 終了時）
      this.boardingOverlayLines = []

      // prevPassengers が door_open 開始時（乗降前）の乗客リスト
      // this.state.passengers が乗降後の乗客リスト
      // prevElevatorFloor を使うため、prevPassengers 記録時の階が必要なので
      // prevPhase が door_open の間は currentFloor は変わらない（移動しない）
      // → state.elevator.currentFloor は乗降後もその階なので使用可能
      const currentFloor = Math.round(this.state.elevator.currentFloor)

      const prevNames = new Set(this.prevPassengers.map(p => p.resident.name))
      const newNames = new Set(this.state.passengers.map(p => p.resident.name))

      // 降りた客: prevPassengers にいて新しい passengers にいない人
      for (const p of this.prevPassengers) {
        if (!newNames.has(p.resident.name)) {
          // p 自身の pressedBy で正解/ミス/通常を判定する
          // auto の客（上の階から乗ってきた1F行き）は pressedBy='auto' で 'normal' 扱い
          // child の悪戯ボタンで止まった場合は 'miss' 扱い（無駄停止）
          const kind =
            p.pressedBy === 'player'
              ? 'correct'
              : p.pressedBy === 'self' || p.pressedBy === 'child'
                ? 'miss'
                : 'normal'
          this.addLog(`${currentFloor}F: ${p.resident.nameZh} ↓`, kind)
        }
      }

      // 乗ってきた客: 新しい passengers にいて prevPassengers にいない人
      for (const p of this.state.passengers) {
        if (!prevNames.has(p.resident.name) && p.pressedBy === 'auto') {
          this.addLog(`${currentFloor}F: ${p.resident.nameZh} ↑1F`, 'normal')
        }
      }
    }
  }

  private addLog(line: string, kind: 'normal' | 'correct' | 'miss'): void {
    this.logLines.push({ text: line, kind })
    if (this.logLines.length > 3) this.logLines.shift()
  }

  // ─── 待機列UI ─────────────────────────────────────────────────

  private refreshWaitingList(): void {
    // 既存テキストを非表示に
    for (const t of this.waitingTextPool) {
      t.visible = false
    }
    // 既存スプライトを非表示に
    for (const s of this.waitingSpritePool) {
      s.visible = false
    }

    const queue = this.state.waitingQueue
    if (queue.length === 0) return

    // 1階のY範囲
    const floor1Y = floorToY(
      1,
      BUILDING_TOP,
      this.floorH,
      this.state.floorCount
    )
    const floor1Bottom = floor1Y + this.floorH
    const midY = floor1Y + this.floorH / 2

    // 表示可能な行数（1行 = 約11px）
    // 1階エリアの下半分（midY 以下）のみを使用し、ボタンや行き先表示との衝突を避ける
    const lineH = 11
    const maxVisible = Math.floor((floor1Bottom - midY) / lineH)
    const displayCount = Math.min(queue.length, MAX_WAITING, maxVisible)
    const hasMore = queue.length > displayCount

    for (let i = 0; i < displayCount; i++) {
      const resident = queue[i]
      const y = midY + i * lineH

      // スプライト
      let s = this.waitingSpritePool[i]
      if (!s) {
        s = new Sprite()
        this.worldContainer.addChild(s)
        this.waitingSpritePool.push(s)
      }
      const resType = resident.type ?? 'normal'
      const texPair =
        this.residentTextures[resType] ?? this.residentTextures['normal']
      s.texture = texPair[this.animFrame]
      s.width = 12
      s.height = 18
      s.anchor.set(0, 0.5)
      s.x = BTN_X - 18
      s.y = y
      s.alpha = 0.7 // プレースホルダは薄く
      s.visible = true

      // テキスト
      let t = this.waitingTextPool[i]
      if (!t) {
        t = new Text({
          text: '',
          style: {
            fontFamily: 'monospace',
            fontSize: 9,
            fill: COLOR_WAITING_TEXT,
          },
        })
        t.anchor.set(0, 0.5)
        this.worldContainer.addChild(t)
        this.waitingTextPool.push(t)
      }
      // テキストプールを通常アイテムとして再利用する際に色を確定させる
      t.style.fill = COLOR_WAITING_TEXT
      t.text = resident.nameZh
      t.x = BTN_X
      t.y = y
      t.visible = true
    }

    if (hasMore) {
      const moreIdx = displayCount
      const extra = queue.length - displayCount
      const y = midY + moreIdx * lineH

      let t = this.waitingTextPool[moreIdx]
      if (!t) {
        t = new Text({
          text: '',
          style: {
            fontFamily: 'monospace',
            fontSize: 9,
            fill: COLOR_WAITING_MORE,
          },
        })
        t.anchor.set(0, 0.5)
        this.worldContainer.addChild(t)
        this.waitingTextPool.push(t)
      }
      // テキストプールを「...他N人」として再利用する際に色を確定させる
      t.style.fill = COLOR_WAITING_MORE
      t.text = `...他${extra}人`
      t.x = BTN_X
      t.y = y
      t.visible = true
    }
  }

  // ─── 乗降ログUI ───────────────────────────────────────────────

  private refreshLogArea(): void {
    this.logGfx.clear()

    for (const t of this.logTextPool) {
      t.visible = false
    }

    if (this.logLines.length === 0) return

    // 1階エリアの右側（NAME_X より右）に縦に表示
    const floor1Y = floorToY(
      1,
      BUILDING_TOP,
      this.floorH,
      this.state.floorCount
    )
    const lineH = 12

    for (let i = 0; i < this.logLines.length; i++) {
      const { text, kind } = this.logLines[i]
      const y = floor1Y + 6 + i * lineH

      let t = this.logTextPool[i]
      if (!t) {
        t = new Text({
          text: '',
          style: { fontFamily: 'monospace', fontSize: 9, fill: COLOR_LOG_TEXT },
        })
        t.anchor.set(0, 0.5)
        this.worldContainer.addChild(t)
        this.logTextPool.push(t)
      }
      const color =
        kind === 'correct'
          ? COLOR_LOG_CORRECT
          : kind === 'miss'
            ? COLOR_LOG_MISS
            : COLOR_LOG_TEXT
      t.style.fill = color
      t.text = text
      t.x = NAME_X
      t.y = y
      t.visible = true
    }
  }

  // ─── 乗降オーバーレイUI ────────────────────────────────────────

  private drawBoardingOverlay(): void {
    const g = this.boardingOverlayGfx
    g.clear()

    // テキストを非表示に
    for (const t of this.boardingOverlayTexts) {
      t.visible = false
    }

    if (this.state.elevator.phase !== 'door_open') return
    if (this.boardingOverlayLines.length === 0) return

    const lineH = 16
    const padding = 6
    const boxW = BOARDING_OVERLAY_W
    const boxH = this.boardingOverlayLines.length * lineH + padding * 2
    const boxX = SHAFT_W + 4
    const boxY = BUILDING_TOP + 4

    // 半透明の黒背景矩形
    g.rect(boxX, boxY, boxW, boxH)
    g.fill({ color: COLOR_OVERLAY_BG, alpha: 0.72 })

    for (let i = 0; i < this.boardingOverlayLines.length; i++) {
      const { text } = this.boardingOverlayLines[i]
      const y = boxY + padding + i * lineH + lineH / 2

      let t = this.boardingOverlayTexts[i]
      if (!t) {
        t = new Text({
          text: '',
          style: { fontFamily: 'monospace', fontSize: 12, fill: LAMP_WARM },
        })
        t.anchor.set(0, 0.5)
        this.worldContainer.addChild(t)
        this.boardingOverlayTexts.push(t)
      }
      t.text = text
      t.x = boxX + padding
      t.y = y
      t.visible = true
    }
  }

  // ─── 入力アタッチ ──────────────────────────────────────────────

  setGameOverCallback(
    cb: (money: number, score: number, mistakes: number) => void
  ): void {
    this.onGameOver = cb
  }

  setClearCallback(
    cb: (money: number, score: number, mistakes: number) => void
  ): void {
    this.onClear = cb
  }

  /** 賃金（受験費用） */
  getMoney(): number {
    return this.state.money
  }

  /** 賢さ（移動中に本を読んだ時間 ms） */
  getScore(): number {
    return this.state.score
  }

  /** テスト用: ログ行リストを返す */
  getLogLines(): ReadonlyArray<{
    text: string
    kind: 'normal' | 'correct' | 'miss'
  }> {
    return this.logLines
  }

  attachInputs(
    keyboard: KeyboardManager,
    _touch: TouchManager,
    onExit: () => void
  ): () => void {
    const handler = (cmd: KeyboardCommand): void => {
      if (cmd === 'cancel') onExit()
    }
    return keyboard.onCommand(handler)
  }

  /** floorCount 変化時にレイアウトを再構築する */
  private rebuildLayout(): void {
    const floorCount = this.state.floorCount
    const totalH = MIN_FLOOR_H * floorCount
    if (totalH <= BUILDING_BOTTOM - BUILDING_TOP) {
      this.floorH = (BUILDING_BOTTOM - BUILDING_TOP) / floorCount
    } else {
      this.floorH = MIN_FLOOR_H
    }

    // 既存の floorBtns を破棄
    for (const btn of this.floorBtns) {
      btn.gfx.destroy()
      btn.labelText.destroy()
      btn.nameText.destroy()
      btn.neonText.destroy()
    }
    this.floorBtns.splice(0)

    // 建物静的グラフィックを再描画
    this.buildingStaticGfx.clear()
    this.drawBuildingStatic()

    // ボタンを再生成
    this.buildFloorButtons()

    // スプライトプールを非表示にリセット
    for (const s of this.waitingSpritePool) {
      s.visible = false
    }
  }

  reset(): void {
    this.state = createInitialState()
    this._gameOverFired = false
    this.prevFloorCount = this.state.floorCount
    // floorCount が初期値に戻るのでレイアウトを再構築する
    this.rebuildLayout()
    // テキストプールとログをリセットしてゲームオーバー後の残留表示を防ぐ
    this.logLines.length = 0
    this.boardingOverlayLines = []
    for (const s of this.waitingSpritePool) {
      s.visible = false
    }
    for (const t of this.waitingTextPool) {
      t.visible = false
    }
    for (const t of this.logTextPool) {
      t.visible = false
    }
    for (const t of this.boardingOverlayTexts) {
      t.visible = false
    }
    this.prevPhase = null
    this.prevPassengers = []
    this.animTimer = 0
    this.animFrame = 0
    this.refreshButtons()
  }

  override destroy(options?: Parameters<Container['destroy']>[0]): void {
    super.destroy(options ?? { children: true })
  }
}
